import {
  customInstanceWithHeader,
  type ResponseWithHeaders,
} from '@fukuri-admins/apis/client/base/axiosInstance';
import {
  getGetApiV1ThreeLetterCodeClientAdminsUsersKey,
  getGetApiV1ThreeLetterCodeClientAdminsUsersUrl,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type {
  GetApiV1ThreeLetterCodeClientAdminsUsers200One,
  GetApiV1ThreeLetterCodeClientAdminsUsersParams,
} from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import type { KeyedMutator } from 'swr';

type Params = {
  f?: GetApiV1ThreeLetterCodeClientAdminsUsersParams['f'];
  keyword?: string;
  page?: number;
};

const fetcher = (threeLetterCode: string, params: Params) =>
  customInstanceWithHeader<GetApiV1ThreeLetterCodeClientAdminsUsers200One>(
    getGetApiV1ThreeLetterCodeClientAdminsUsersUrl(threeLetterCode, params),
    { method: 'GET' }
  );

type Args = {
  threeLetterCode: string;
  f?: GetApiV1ThreeLetterCodeClientAdminsUsersParams['f'];
  keyword?: string;
  page?: number;
};

type IUseGetClientUsers = (args: Args) => {
  data: GetApiV1ThreeLetterCodeClientAdminsUsers200One;
  totalPage: number;
  totalCount: number;
  mutate: KeyedMutator<ResponseWithHeaders<GetApiV1ThreeLetterCodeClientAdminsUsers200One>>;
};

/**
 * 企業管理者ユーザー一覧取得フック
 *
 * - ページネーションのメタ情報（total-pages / total-count）はレスポンスヘッダから取得する
 * - f='pending' で初回ログイン未完了者に絞り込む
 */
export const useGetClientUsers: IUseGetClientUsers = ({ threeLetterCode, f, keyword, page }) => {
  const params: Params = { f, keyword: keyword || undefined, page };

  const { data: response, mutate } = useFetch<
    ResponseWithHeaders<GetApiV1ThreeLetterCodeClientAdminsUsers200One>
  >(
    getGetApiV1ThreeLetterCodeClientAdminsUsersKey(threeLetterCode, params),
    () => fetcher(threeLetterCode, params),
    { suspense: true }
  );

  if (!response) {
    return { data: { users: [] }, totalPage: 0, totalCount: 0, mutate };
  }

  const data = response.data;
  const totalPage = Number(response.headers['total-pages']) || 0;
  const totalCount = Number(response.headers['total-count']) || 0;

  return { data, totalPage, totalCount, mutate };
};
